import { useQuery } from "@apollo/client";
import type { ClassValue } from "clsx";
import KpisChart from "components/charts/modern/KpisChart";
import FilterSelector from "components/charts/modern/inputs/FilterSelector";
import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import UnlockPremiumContentCTA, { TrialCTAContentForChart } from "components/conversion/UnlockPremiumContentCTA";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import KpisQuery from "components/financials/modern/graphql/KpisQuery";
import type {
  IKpisQuery,
  IKpisQueryVariables,
} from "components/financials/modern/graphql/__generated__/KpisQuery.generated";
import formatKpiValue from "components/financials/modern/kpis/formatKpiValue";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import ReactSelect from "components/ui/ReactSelect";
import { ScrollArea } from "components/ui/ScrollArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import { Separator } from "components/ui/Separator";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import parseISO from "date-fns/parseISO";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";
import orderBy from "lodash/orderBy";
import {
  ActivityIcon,
  AsteriskIcon,
  BarChartIcon,
  BinaryIcon,
  DollarSignIcon,
  EyeIcon,
  InfoIcon,
  PercentIcon,
  SlashIcon,
} from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface KpisProps {
  tickerSymbol: string;
  display: "chart" | "table" | "documention";
}

export default function Kpis({ display, tickerSymbol }: KpisProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const amplitude = useAmplitude();
  const formattedKpisTitle = {
    metric: intl.formatMessage({ defaultMessage: "Métrica", id: "kg/wta", description: "Tipo de KPI" }),
    number: intl.formatMessage({ defaultMessage: "Número", id: "X53X0L", description: "Tipo de KPI" }),
    currency: intl.formatMessage({ defaultMessage: "$", id: "HzMRF6", description: "Tipo de KPI" }),
    percentage: intl.formatMessage({ defaultMessage: "%", id: "avDHVQ", description: "Tipo de KPI" }),
  };

  const { data, loading } = useQuery<IKpisQuery, IKpisQueryVariables>(KpisQuery, {
    variables: {
      tickerSymbol,
      language: intl.locale,
    },
  });

  const [selectedKpis, setSelectedKpis] = useState<string[]>([]);
  const [plotType, setPlotType] = useState<"area" | "column">("column");
  const [filterByType, setFilterByType] = useState<"all" | "number" | "percent" | "currency" | "ratio">("all");
  const [filterByRelevance, setFilterByRelevance] = useState<"all" | "importantOnly">("all");
  const [statementType, setStatementType] = useState<IFinancialsStatementType>(IFinancialsStatementType.Annual);
  const [yearsLimit, setYearsLimit] = useState<5 | 10 | 15>(10);

  const isAnnual = statementType === IFinancialsStatementType.Annual;
  // const isQuarterly = statementType === IFinancialsStatementType.Quarterly;
  const reportDates = (isAnnual ? data?.kpis?.reportDates.annual : data?.kpis?.reportDates.quarterly) || [];
  const kpis = ((isAnnual ? data?.kpis?.annual : data?.kpis?.quarterly) || [])
    .filter((candidate) => {
      if (filterByRelevance === "importantOnly") {
        return candidate.isImportant;
      }
      return true;
    })
    .filter((candidate) => {
      if (filterByType === "currency") {
        return candidate.format === "number" && candidate.isCurrency;
      }
      if (filterByType === "number") {
        return candidate.format === "number" && !candidate.isCurrency;
      }
      if (filterByType === "percent") {
        return candidate.format === "%";
      }
      if (filterByType === "ratio") {
        return candidate.format === "ratio";
      }
      return true;
    })
    .map((kpi) => {
      return {
        ...kpi,
        order: ["number", "currency", "%", "ratio"].indexOf(kpi.isCurrency ? "currency" : kpi.format),
      };
    });

  const sortedKpis = orderBy(kpis, ["isImportant", "order"], ["desc", "asc"]).filter((kpi) => {
    return kpi.series.some((value) => value);
  });

  if (loading) {
    return <LoadingSkeleton count={1} className="h-56" />;
  }

  return (
    <div className="h-full w-full space-y-4">
      {!selfie?.isPremium && (
        <>
          <p
            className={classNames(
              "flex flex-col items-center gap-2 rounded-xl bg-teal-50 p-3 text-center text-sm font-medium text-teal-600",
              "dark:bg-teal-950 dark:text-teal-300",
              "md:inline-flex md:w-full md:flex-row md:text-left",
            )}
          >
            <InfoIcon className="size-6 lg:size-4" />
            <FormattedMessage
              id="8vw6zm"
              defaultMessage="Os KPIs são liberados apenas no Premium! <link>Confira todos os detalhes</link>"
              values={{
                // eslint-disable-next-line react/no-unstable-nested-components
                link: (children) => {
                  return (
                    <Link
                      as="/premium"
                      href="/premium"
                      className="text-blue-600 underline-offset-2 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                      onClick={() => {
                        amplitude.logEvent("Click on link to become Premium", {
                          origin: "KPIs",
                        });
                      }}
                    >
                      {children}
                    </Link>
                  );
                },
              }}
            />
          </p>
          <Separator />
        </>
      )}

      <div className="flex w-full flex-col items-center gap-x-4 gap-y-4 sm:flex-row">
        {["chart", "table"].includes(display) && (
          <div className="w-full sm:w-auto">
            <SelectStatementType
              disabled={!selfie || !selfie.isPremium || selfie.isTrialing}
              statementType={statementType}
              onChangeStatementType={setStatementType}
            />
          </div>
        )}
        {display === "table" && (
          <Select
            disabled={!selfie?.isPremium}
            value={String(yearsLimit)}
            onValueChange={(value) => setYearsLimit(Number(value) as 5 | 10 | 15)}
          >
            <SelectTrigger
              className={classNames(
                "w-full sm:w-28",
                "border-none bg-white",
                "font-aeonik text-sm font-medium tracking-wide text-slate-600",
                "ring-1 ring-inset ring-slate-200",
                "hover:bg-slate-50",
                "focus-visible:border-transparent focus-visible:ring-slate-100 focus-visible:ring-offset-slate-300",
                "dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-950",
                "dark:hover:bg-slate-600 dark:hover:ring-slate-900",
                "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className={classNames(
                "min-w-28 rounded-xl border border-slate-200 text-sm",
                "dark:border-slate-900 dark:bg-slate-800 dark:text-slate-300",
              )}
            >
              <SelectItem value="5">5 {intl.formatMessage({ defaultMessage: "anos", id: "4eyg/s" })}</SelectItem>
              <SelectItem value="10">10 {intl.formatMessage({ defaultMessage: "anos", id: "4eyg/s" })}</SelectItem>
              <SelectItem value="15">15 {intl.formatMessage({ defaultMessage: "anos", id: "4eyg/s" })}</SelectItem>
            </SelectContent>
          </Select>
        )}

        <div className={classNames("w-full", "space-y-4 sm:flex sm:w-auto sm:flex-row sm:gap-x-4 lg:space-y-0")}>
          {display === "chart" && (
            <PlotTypeSelector
              onChange={setPlotType}
              value={plotType}
              types={[
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon className="h-5 w-5" />,
                },
                {
                  type: "area",
                  label: intl.formatMessage({
                    id: "oaRYWa",
                    defaultMessage: "Linha",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <ActivityIcon className="h-5 w-5" />,
                },
              ]}
            />
          )}

          {["documention", "table"].includes(display) && (
            <>
              <FilterSelector<"number" | "percent" | "ratio" | "currency" | "all">
                onChange={(type) => setFilterByType(type)}
                value={filterByType}
                types={[
                  {
                    type: "all",
                    label: intl.formatMessage({
                      id: "7kU5RP",
                      defaultMessage: "Todos",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <EyeIcon className="h-5 w-5" />,
                  },
                  {
                    type: "number",
                    label: intl.formatMessage({
                      id: "GsxTY+",
                      defaultMessage: "Número",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <BinaryIcon className="h-5 w-5" />,
                  },
                  {
                    type: "currency",
                    label: intl.formatMessage({
                      id: "e/dBk7",
                      defaultMessage: "Financeiro",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <DollarSignIcon className="h-5 w-5" />,
                  },
                  {
                    type: "percent",
                    label: intl.formatMessage({
                      id: "fl0DF2",
                      defaultMessage: "%",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <PercentIcon className="h-5 w-5" />,
                  },
                  {
                    type: "ratio",
                    label: intl.formatMessage({
                      id: "1jEsn8",
                      defaultMessage: "Ratio",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <SlashIcon className="h-5 w-5" />,
                  },
                ]}
              />
              <FilterSelector<"all" | "importantOnly">
                onChange={(filterByRelevance) => setFilterByRelevance(filterByRelevance)}
                value={filterByRelevance}
                types={[
                  {
                    type: "all",
                    label: intl.formatMessage({
                      id: "7kU5RP",
                      defaultMessage: "Todos",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <EyeIcon className="h-5 w-5" />,
                  },
                  {
                    type: "importantOnly",
                    label: intl.formatMessage({
                      id: "qC3jw1",
                      defaultMessage: "Importantes",
                      description: "Filtro do tipo de KPIs",
                    }),
                    icon: <AsteriskIcon className="h-5 w-5" />,
                  },
                ]}
              />
            </>
          )}
        </div>

        {display === "chart" && sortedKpis.length > 0 && (
          <div className="w-full">
            <ReactSelect
              isDisabled={!selfie?.isPremium}
              value={selectedKpis.map((value) => ({
                label: value,
                value,
              }))}
              noOptionsMessage={() => intl.formatMessage({ defaultMessage: "Sem opções", id: "psj49M" })}
              placeholder={intl.formatMessage({ defaultMessage: "Selecione até 2 KPIs", id: "/T1coD" })}
              options={sortedKpis.map((node) => {
                let formatTitle = formattedKpisTitle.number;
                if (node.isCurrency) {
                  formatTitle = formattedKpisTitle.currency;
                }
                if (node.format === "ratio") {
                  formatTitle = formattedKpisTitle.metric;
                }
                if (node.format === "%") {
                  formatTitle = formattedKpisTitle.percentage;
                }
                return {
                  value: node.name,
                  label: `${node.name} (${formatTitle})`,
                };
              })}
              onChange={(options) => {
                const allOptions = options.map((node) => node.value);
                if (allOptions.length > 2) {
                  setSelectedKpis([allOptions[0], allOptions[allOptions.length - 1]]);
                  return;
                }
                setSelectedKpis(allOptions);
              }}
            />
          </div>
        )}
      </div>

      {display === "chart" && selectedKpis.length > 0 && (
        <KpisChart
          statementType={statementType}
          currency={data?.kpis?.currency || ""}
          plotType={plotType}
          kpis={selectedKpis.map((selectedKpi) => {
            const kpi = sortedKpis.find((kpi) => kpi.name === selectedKpi);
            if (!kpi) {
              throw new Error("KPI not found");
            }

            return {
              name: kpi.name,
              format: kpi.format,
              isCurrency: kpi.isCurrency,
              data: kpi.series.map((value, index) => {
                return {
                  x: parseISO(reportDates[index]).getTime(),
                  y: value,
                };
              }),
            };
          })}
        />
      )}

      {kpis.length === 0 && (
        <p
          className={classNames(
            "inline-flex flex-col items-center gap-2 rounded-xl bg-slate-100 p-3 text-center text-sm font-medium text-slate-600",
            "dark:bg-slate-800 dark:text-slate-300",
            "sm:w-full sm:flex-row sm:text-left",
          )}
        >
          <InfoIcon className="size-6 lg:size-4" />
          <FormattedMessage
            id="nF6ZQr"
            defaultMessage="Nenhum KPI foi encontrado. Em alguns casos a empresa não possui nenhum KPI ou os filtros não batem."
          />
        </p>
      )}

      {display === "table" && kpis.length > 0 && selfie?.isPremium && (
        <>
          <ScrollArea type="always" orientation="horizontal" className="relative h-full w-full">
            <div className="flex flex-col gap-y-4 pb-4 lg:w-full">
              <div
                className={classNames(
                  "relative overflow-auto rounded-xl border border-slate-200",
                  "dark:border-slate-950",
                )}
              >
                <table className="w-full divide-y divide-slate-200 dark:divide-slate-950">
                  <thead>
                    <tr className="divide-x divide-slate-200 text-sm dark:divide-slate-950">
                      <th className="min-w-20 max-w-24 bg-stone-100 p-2 text-sm text-stone-600 dark:bg-slate-900 dark:text-slate-300">
                        <FormattedMessage defaultMessage="Ano" id="wY7G7a" />
                      </th>
                      {sortedKpis.map((kpi) => {
                        return (
                          <th
                            key={kpi.name}
                            className={classNames(
                              "w-96 min-w-60  p-2 text-center text-sm font-medium tracking-wide text-slate-600",
                              "dark:text-slate-300",
                              getKpiColumnClassNames(kpi),
                              {
                                "font-bold": kpi.isImportant,
                              },
                            )}
                          >
                            {kpi.description ? (
                              <SimpleTextTooltip
                                content={
                                  intl.locale.startsWith("pt") ? (
                                    <>
                                      <strong className="font-medium text-orange-300 dark:text-orange-400">
                                        {kpi.kpi}:
                                      </strong>{" "}
                                      <strong className="font-medium">{kpi.description}</strong>
                                    </>
                                  ) : (
                                    kpi.description
                                  )
                                }
                                triggerClassName="justify-center"
                              >
                                <span>{kpi.name}</span>
                              </SimpleTextTooltip>
                            ) : (
                              <span>{kpi.name}</span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-950">
                    {reportDates
                      .map((reportDate, index) => ({ reportDate, originalIndex: index }))
                      .reverse()
                      .slice(-yearsLimit)
                      .map(({ reportDate, originalIndex }) => {
                        const isTTM = isAnnual && data?.kpis?.reportDates.isTrailingTwelveMonths[originalIndex];
                        return (
                          <tr key={reportDate} className="divide-x divide-slate-200 dark:divide-slate-950">
                            <th
                              className={classNames(
                                "min-w-20 max-w-24 whitespace-nowrap bg-slate-50 p-2 text-sm font-medium tracking-wide text-slate-600",
                                "dark:bg-slate-900 dark:text-slate-300 dark:group-hover:bg-slate-900 dark:group-hover:text-white",
                                {
                                  "bg-orange-400/10 text-orange-700 dark:bg-orange-400/10 dark:text-orange-300": isTTM,
                                },
                              )}
                            >
                              {isTTM
                                ? "TTM"
                                : intl.formatDate(parseISO(reportDate), {
                                    year: "numeric",
                                    month: "numeric",
                                  })}
                            </th>
                            {sortedKpis.map((kpi) => {
                              const value = kpi.series[originalIndex];
                              return (
                                <td
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`${kpi.name}/${value}/${originalIndex}`}
                                  className={classNames(
                                    "p-2 text-center font-mono text-sm font-medium dark:text-slate-300",
                                    {
                                      "bg-red-600/5 font-bold text-red-600 dark:bg-red-600/10 dark:text-red-200":
                                        value && value < 0,
                                    },
                                  )}
                                >
                                  {selfie?.isPremium ? (
                                    formatKpiValue(value, data?.kpis?.currency || "", kpi.format, kpi.isCurrency)
                                  ) : (
                                    <div className="flex items-center justify-center">
                                      <span className="inline-flex h-4 w-20 rounded-xl bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollArea>

          {selfie?.isTrialing && <UnlockPremiumContentCTA origin="KPIs" />}
        </>
      )}

      {["chart", "documention"].includes(display) && (
        <ul className="flex flex-col gap-y-2">
          {sortedKpis
            .filter((kpi) => {
              if (display === "chart") {
                return selectedKpis.includes(kpi.name);
              }
              return true;
            })
            .map((kpi) => {
              let type = formattedKpisTitle.number;
              if (kpi.isCurrency) {
                type = formattedKpisTitle.currency;
              }
              if (kpi.format === "ratio") {
                type = formattedKpisTitle.metric;
              }
              if (kpi.format === "%") {
                type = formattedKpisTitle.percentage;
              }
              return (
                <li
                  key={kpi.name}
                  className={classNames("rounded-xl bg-slate-50 p-3 dark:bg-slate-900", {
                    "bg-blue-50/50 dark:bg-sky-400/10": kpi.isImportant,
                  })}
                >
                  <div className="flex items-center gap-x-2">
                    {intl.locale.startsWith("pt") ? (
                      <SimpleTextTooltip content={kpi.kpi} triggerClassName="justify-center">
                        <h2
                          className={classNames("text-sm font-bold text-slate-800 dark:text-slate-300", {
                            "text-blue-800 dark:text-sky-400": kpi.isImportant,
                          })}
                        >
                          {kpi.name}
                        </h2>
                      </SimpleTextTooltip>
                    ) : (
                      <h2
                        className={classNames("text-sm font-bold text-slate-800 dark:text-slate-300", {
                          "text-blue-800 dark:text-sky-400": kpi.isImportant,
                        })}
                      >
                        {kpi.name}
                      </h2>
                    )}

                    <div className="inline-flex gap-x-1">
                      <span
                        className={classNames(
                          "rounded-md bg-teal-700/10 px-1.5 py-0.5 text-xs font-bold text-teal-600",
                          getKpiTagClassNames(kpi),
                        )}
                      >
                        {type}
                      </span>
                      {kpi.isImportant && (
                        <span
                          className={classNames(
                            "rounded-md bg-sky-800 px-1.5 py-0.5 text-xs font-bold text-white dark:bg-sky-300/10 dark:text-sky-50",
                          )}
                        >
                          <FormattedMessage defaultMessage="Importante" id="3J8262" />
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm tracking-wide text-slate-600 dark:text-slate-400">{kpi.description}</p>
                </li>
              );
            })}
        </ul>
      )}

      {display === "chart" && selectedKpis.length > 0 && selfie?.isTrialing && (
        <UnlockPremiumContentCTA
          origin="KPIs"
          freeMessage={intl.formatMessage({
            defaultMessage: "Para ver o gráfico completo dos KPIs, você precisa ser Premium!",
            id: "od27xD",
          })}
          trialMessage={<TrialCTAContentForChart />}
        />
      )}
    </div>
  );
}

function getKpiColumnClassNames(kpi: NonNullable<IKpisQuery["kpis"]>["annual"][0]): ClassValue {
  return classNames({
    "bg-lime-400/10 text-lime-600 dark:bg-lime-950 dark:text-lime-100": kpi.format === "number" && !kpi.isCurrency,
    "bg-teal-400/10 text-teal-600 dark:bg-teal-950 dark:text-teal-100": kpi.format === "number" && kpi.isCurrency,
    "bg-sky-400/10 text-sky-600 dark:bg-sky-950 dark:text-sky-100": kpi.format === "%",
    "bg-red-400/10 text-red-600 dark:bg-rose-950 dark:text-rose-100": kpi.format === "ratio",
    "bg-violet-400/10 text-violet-600 dark:bg-purple-950 dark:text-purple-100": kpi.isImportant,
  });
}

function getKpiTagClassNames(kpi: NonNullable<IKpisQuery["kpis"]>["annual"][0]): ClassValue {
  return classNames({
    "bg-lime-400/10 text-lime-600 dark:bg-lime-800/30 dark:text-lime-100": kpi.format === "number" && !kpi.isCurrency,
    "bg-teal-400/10 text-teal-600 dark:bg-teal-800/30 dark:text-teal-100": kpi.format === "number" && kpi.isCurrency,
    "bg-sky-400/10 text-sky-600 dark:bg-sky-800/30 dark:text-sky-100": kpi.format === "%",
    "bg-red-400/10 text-red-600 dark:bg-rose-800/30 dark:text-rose-100": kpi.format === "ratio",
    "bg-violet-400/10 text-violet-600 dark:bg-purple-800/30 dark:text-purple-100": kpi.isImportant,
  });
}
